import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Thermometer, Droplets, Activity, Zap, Wifi, WifiOff, Settings, Plus } from 'lucide-react';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';
import SensorGauge from '../components/SmartKitchen/SensorGauge';
import RealTimeChart from '../components/SmartKitchen/RealTimeChart';
import DeviceCard from '../components/SmartKitchen/DeviceCard';
import { useWebSocket } from '../hooks/useWebSocket';

interface SensorData {
  timestamp: number;
  device_id: string;
  metric: string;
  value: number;
}

interface Device {
  id: string;
  name: string;
  type: 'oven' | 'fridge' | 'scale' | 'thermometer';
  status: 'online' | 'offline';
  lastUpdate: number;
  sensors: {
    temperature?: number;
    humidity?: number;
    pH?: number;
    weight?: number;
  };
}

const SmartKitchen: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([
    {
      id: 'oven_123',
      name: 'Smart Oven',
      type: 'oven',
      status: 'online',
      lastUpdate: Date.now(),
      sensors: { temperature: 180, humidity: 45 }
    },
    {
      id: 'fridge_456',
      name: 'Smart Fridge',
      type: 'fridge',
      status: 'online',
      lastUpdate: Date.now(),
      sensors: { temperature: 4, humidity: 85 }
    },
    {
      id: 'scale_789',
      name: 'Kitchen Scale',
      type: 'scale',
      status: 'online',
      lastUpdate: Date.now(),
      sensors: { weight: 250 }
    }
  ]);

  const [sensorHistory, setSensorHistory] = useState<SensorData[]>([]);
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);

  const { socket, sendMessage } = useWebSocket('ws://localhost:3001', {
    onOpen: () => setIsConnected(true),
    onClose: () => setIsConnected(false),
    onMessage: (data) => {
      if (data.type === 'sensor_update') {
        updateDeviceSensor(data.device_id, data.metric, data.value);
        setSensorHistory(prev => [...prev.slice(-99), data]);
      } else if (data.type === 'ai_suggestion') {
        setAiSuggestion(data.suggestion);
      }
    }
  });

  const updateDeviceSensor = (deviceId: string, metric: string, value: number) => {
    setDevices(prev => prev.map(device => 
      device.id === deviceId 
        ? { 
            ...device, 
            sensors: { ...device.sensors, [metric]: value },
            lastUpdate: Date.now()
          }
        : device
    ));
  };

  const requestAISuggestion = async () => {
    try {
      const response = await fetch('/api/smart-kitchen/ai-suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          devices: devices.map(d => ({
            id: d.id,
            type: d.type,
            sensors: d.sensors
          })),
          context: 'cooking_assistance'
        })
      });
      
      const data = await response.json();
      setAiSuggestion(data.suggestion);
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);
    }
  };

  const addDevice = () => {
    // Mock adding a new device
    const newDevice: Device = {
      id: `device_${Date.now()}`,
      name: 'New Device',
      type: 'thermometer',
      status: 'offline',
      lastUpdate: Date.now(),
      sensors: {}
    };
    setDevices(prev => [...prev, newDevice]);
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <Activity className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Smart <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Kitchen
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Monitor your kitchen devices in real-time with IoT sensors and AI-powered cooking assistance.
            Get intelligent recommendations based on live environmental data.
          </p>
        </motion.div>

        {/* Connection Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8"
        >
          <GlassCard className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {isConnected ? (
                  <>
                    <Wifi className="w-6 h-6 text-green-400" />
                    <span className="text-white font-medium">Connected to Smart Kitchen</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-6 h-6 text-red-400" />
                    <span className="text-white font-medium">Connecting to devices...</span>
                  </>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={addDevice}
                  className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Device</span>
                </button>
                <button className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Device Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
        >
          {devices.map((device, index) => (
            <FloatingElement key={device.id} delay={index * 0.1}>
              <DeviceCard device={device} />
            </FloatingElement>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Real-Time Monitoring */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Activity className="w-6 h-6 text-blue-400 mr-2" />
                Real-Time Monitoring
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <SensorGauge
                  title="Oven Temperature"
                  value={devices.find(d => d.id === 'oven_123')?.sensors.temperature || 0}
                  max={250}
                  unit="°C"
                  color="from-red-500 to-orange-500"
                  icon={<Thermometer className="w-5 h-5" />}
                />
                <SensorGauge
                  title="Humidity"
                  value={devices.find(d => d.id === 'fridge_456')?.sensors.humidity || 0}
                  max={100}
                  unit="%"
                  color="from-blue-500 to-cyan-500"
                  icon={<Droplets className="w-5 h-5" />}
                />
              </div>

              <RealTimeChart data={sensorHistory} />
            </GlassCard>
          </motion.div>

          {/* AI Cooking Assistant */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Zap className="w-6 h-6 text-yellow-400 mr-2" />
                AI Cooking Assistant
              </h2>
              
              <div className="space-y-4">
                <button
                  onClick={requestAISuggestion}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all duration-200"
                >
                  Get Smart Cooking Suggestion
                </button>
                
                {aiSuggestion && (
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <h3 className="text-white font-semibold mb-2">AI Recommendation:</h3>
                    <p className="text-purple-200">{aiSuggestion}</p>
                  </div>
                )}
                
                <div className="space-y-3">
                  <h3 className="text-white font-semibold">Current Conditions:</h3>
                  {devices.map(device => (
                    <div key={device.id} className="bg-white/5 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-purple-300">{device.name}</span>
                        <span className={`text-sm px-2 py-1 rounded-full ${
                          device.status === 'online' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                        }`}>
                          {device.status}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-purple-200">
                        {Object.entries(device.sensors).map(([key, value]) => (
                          <span key={key} className="mr-4">
                            {key}: {value}{key === 'temperature' ? '°C' : key === 'humidity' ? '%' : key === 'weight' ? 'g' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>

        {/* Cooking Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-white mb-6">Recent Cooking Sessions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'Sourdough Bread', duration: '4h 30m', temp: '220°C', status: 'completed' },
                { name: 'Chocolate Cake', duration: '1h 15m', temp: '180°C', status: 'in_progress' },
                { name: 'Roast Chicken', duration: '2h 45m', temp: '200°C', status: 'completed' }
              ].map((session, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h3 className="text-white font-semibold mb-2">{session.name}</h3>
                  <div className="space-y-1 text-sm text-purple-300">
                    <p>Duration: {session.duration}</p>
                    <p>Temperature: {session.temp}</p>
                    <p className={`capitalize ${
                      session.status === 'completed' ? 'text-green-400' : 'text-yellow-400'
                    }`}>
                      {session.status.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default SmartKitchen;